import { useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CareerManager({ career, onRefresh, headers }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ title: '', company: '', period: '', description: '', order: 0 });

    const handleSave = async () => {
        try {
            if (editItem) {
                await axios.put(`${API}/career/${editItem.id}`, formData, { headers });
                toast.success('Career entry updated!');
            } else {
                await axios.post(`${API}/career`, formData, { headers });
                toast.success('Career entry created!');
            }
            setDialogOpen(false);
            setEditItem(null);
            setFormData({ title: '', company: '', period: '', description: '', order: 0 });
            onRefresh();
        } catch (error) {
            toast.error('Failed to save');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this career entry?')) return;
        try {
            await axios.delete(`${API}/career/${id}`, { headers });
            toast.success('Career entry deleted!');
            onRefresh();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const openEdit = (item) => {
        setEditItem(item);
        setFormData({ title: item.title, company: item.company, period: item.period, description: item.description, order: item.order });
        setDialogOpen(true);
    };

    return (
        <div className="retro-card p-6" data-testid="career-manager">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                    Manage Career
                </h2>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn text-xs uppercase tracking-widest" data-testid="add-career-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Entry
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none border-2 max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                                {editItem ? 'Edit Career Entry' : 'New Career Entry'}
                            </DialogTitle>
                            <DialogDescription>Fill in the details below</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Job Title</Label>
                                <Input className="rounded-none border-2 mt-1" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} data-testid="career-title-input" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Company</Label>
                                <Input className="rounded-none border-2 mt-1" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} data-testid="career-company-input" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Period</Label>
                                <Input className="rounded-none border-2 mt-1" value={formData.period} onChange={(e) => setFormData({...formData, period: e.target.value})} placeholder="e.g., 2020 - Present" data-testid="career-period-input" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Description</Label>
                                <Textarea className="rounded-none border-2 mt-1" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} data-testid="career-description-input" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Display Order (lower = first)</Label>
                                <Input className="rounded-none border-2 mt-1" type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setDialogOpen(false); setEditItem(null); }} className="rounded-none border-2">Cancel</Button>
                            <Button onClick={handleSave} className="rounded-none border-2 border-primary bg-primary text-primary-foreground" data-testid="save-career-btn">Save</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {career.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No career entries yet. Add your journey!</p>
            ) : (
                <div className="space-y-4">
                    {career.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-4 border-2 border-border" data-testid={`career-row-${item.id}`}>
                            <div className="flex-1">
                                <h3 className="font-bold">{item.title}</h3>
                                <p className="text-sm text-primary">{item.company} • {item.period}</p>
                                <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="icon" onClick={() => openEdit(item)} className="rounded-none border-2" data-testid={`edit-career-${item.id}`}><Edit className="h-4 w-4" /></Button>
                                <Button variant="outline" size="icon" onClick={() => handleDelete(item.id)} className="rounded-none border-2 text-destructive" data-testid={`delete-career-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
